import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  getOfferEnrollmentClinicsAction,
  updateClinicOfferEnrollmentStatusAction,
  getProviderServiceStatusAction,
  updateProviderServiceStatusAction
} from "actions/ProviderActions";
import ListItem from "components/common/ListItem/ListItem";
import EnrollmentConfirmModal from "./EnrollmentConfirmModal";
import SpinnerTakeover from "components/common/SpinnerTakeover";
import styles from "./OfferEnrollment.scss";
import { FOREVER_FELINE_SNAP, FOREVER_FELINE_CHEM } from 'constants/ProviderEnrollment';

function OfferEnrollment(props) {
  const {
    clinics,
    isLoading,
    nodeId,
    getOfferEnrollmentClinics,
    updateProviderServiceStatus,
    updateStatusAction,
    programService,
  } = props;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [enrolledIds, setEnrolledIds] = useState([]);
  const [isEmptyState, setisEmptyState] = useState(false);
  const [isFinalizingEarly, setIsFinalizingEarly] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [offerDescription, setDescription] = useState("");


  useEffect(() => {
    if (programService && nodeId) {
      getOfferEnrollmentClinics(programService.programServiceId, nodeId);
      if (programService.programServiceId === 30) {
        setDescription(FOREVER_FELINE_SNAP.description);
        setPdf(FOREVER_FELINE_SNAP.pdf);
      } else if (programService.programServiceId === 32) {
        setDescription(FOREVER_FELINE_CHEM.description);
        setPdf(null);
      }
    }
  }, [programService, nodeId]);

  useEffect(() => {
    if (!isLoading) {
      if (clinics?.length > 0) {
        setisEmptyState(false);
        const enrolledClinics = clinics
          .filter((clinic) => clinic.serviceStatus === "Enrolled")
          .map((clinic) => clinic.clinicId);
        setEnrolledIds(enrolledClinics);
      } else {
        setisEmptyState(true);
      }
    }
  }, [isLoading]);

  const handleEnroll = (clinic) => {
    setSelectedClinic(clinic);
    setIsFinalizingEarly(false);
    setShowConfirmModal(true);
  };

  const handleFinalize = () => {
    setIsFinalizingEarly(true);
    setShowConfirmModal(true);
  };

  const handleModalConfirm = async (data, isFinalizedEarly, clinicId) => {
    const updatedEnrolledCount = enrolledIds.length + 1;

    if (isFinalizedEarly) {
      await updateProviderServiceStatus(programService.programServiceId, nodeId, data);
      setIsFinalized(true);
    } else if (updatedEnrolledCount >= programService.configJson?.MaxClinicEnrollmentsPerRep || updatedEnrolledCount == clinics.length) {
      await updateStatusAction(clinicId, programService.programServiceId, data);
      await updateProviderServiceStatus(programService.programServiceId, nodeId, data);
      setIsFinalized(true);
    } else {
      await updateStatusAction(clinicId, programService.programServiceId, data);
      setIsFinalized(false);
    }

    setShowConfirmModal(false);
    await getOfferEnrollmentClinics(programService.programServiceId, nodeId);
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      <div className={styles.enrollmentContainer}>
        <h1>{programService.programServiceName}</h1>
        {!programService.isFinalized && clinics.length > 0 && (
          <p className={styles.description}>
            {offerDescription}{" "}
            {pdf && (
              <a
                href={pdf}
                target="_blank"
                rel="noopener noreferrer"
              >
                detailer and terms and conditions.
              </a>
            )}
          </p>
        )}
        {!programService.isFinalized && !isFinalized && !isEmptyState && (
          <div className={styles.progressContainer}>
            <span className={styles.progressText}>
              {enrolledIds.length} of {programService.configJson?.MaxClinicEnrollmentsPerRep} clinics selected
            </span>
            <button
              className={styles.finalizeButton}
              onClick={handleFinalize}
              disabled={enrolledIds.length === 0}
            >
              Finalize Enrollment
            </button>
          </div>
        )}

        <div className={styles.listItemContainer}>
          {isLoading ? (
            <div></div>
          ) : isEmptyState ? (
            <div className={styles.emptyState}>
              <p>You do not have any clinics eligible to participate in this offer. Please check back for future offers that your clinics can participate in.</p>
            </div>
          ) : (isFinalized || programService.isFinalized || enrolledIds.length === programService.configJson?.MaxClinicEnrollmentsPerRep) ? (
            <div className={styles.successState}>
              <i className="fa fa-check"></i>
              <div className={styles.successHeader}>Complete</div>
              <div className={styles.successDescription}>
                You’ve successfully enrolled {enrolledIds.length} clinic{enrolledIds.length > 1 ? 's' : ''}. Greenline customer service will contact your
                clinics within 24 hours of enrollment to set up the offer.
              </div>
              <div className={styles.successEnrolledClinics}>
                {enrolledIds.map((id) => {
                  const clinic = clinics.find((clinic) => clinic.clinicId === id);
                  return clinic ? <div key={clinic.clinicId}>{clinic.clinicName}</div> : null;
                })}
              </div>
            </div>
          ) : (
            clinics
              .filter(clinic => !!clinic.clinicId)
              .map((clinic) => (
                <ListItem
                  key={clinic.clinicId}
                  entityId={clinic.clinicId}
                  entityName={clinic.clinicName}
                  isEnrolled={enrolledIds.includes(clinic.clinicId)}
                  onButtonClick={() => handleEnroll(clinic)}
                  buttonText={"Enroll"}
                />
              ))
          )}
        </div>

        {showConfirmModal && (
          <EnrollmentConfirmModal
            show={showConfirmModal}
            onClose={handleCloseModal}
            onConfirm={handleModalConfirm}
            clinic={selectedClinic}
            isFinalizingEarly={isFinalizingEarly}
            enrolledClinicCount={enrolledIds.length}
            updateStatusAction={updateStatusAction}
            program={programService}
          />
        )}
      </div>

      <SpinnerTakeover show={isLoading} />
    </>
  );
}


export default connect(
    (state) => {
         const userProfile = state.user.userProfile;
         const currentOrgNode = state.entities.orgNode?.current;

      return {
        clinics: state.entities.offerEnrollment?.clinics || [],
        isLoading: state.entities.offerEnrollment?.isLoading || false,
        userProfile,
        currentOrgNode,
      };

    }, (dispatch) => ({
      getOfferEnrollmentClinics: (programServiceId, nodeId) =>
        dispatch(getOfferEnrollmentClinicsAction(programServiceId, nodeId)),
      updateStatusAction: (clinicId, programServiceId, data) =>
        dispatch(updateClinicOfferEnrollmentStatusAction(clinicId, programServiceId, data)),
      getProviderServiceStatus: (nodeId) =>
        dispatch(getProviderServiceStatusAction(nodeId)),
      updateProviderServiceStatus: (programServiceId, nodeId, data) =>
        dispatch(updateProviderServiceStatusAction(programServiceId, nodeId, data)),
    }),

    )(OfferEnrollment);